import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Eye, TrendUp, CurrencyGbp, Crown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatTile = ({ icon: Icon, label, value, hint, accent = 'text-gold' }) => (
  <div className="panel-luxe p-5 flex items-start gap-4">
    <Icon size={22} weight="duotone" className={`${accent} shrink-0 mt-0.5`} />
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-1">{label}</p>
      <p className="text-2xl font-serif font-normal text-ivory leading-none">{value}</p>
      {hint && <p className="text-[11px] text-ash mt-1.5">{hint}</p>}
    </div>
  </div>
);

export const AdminStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/stats`, { withCredentials: true })
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="mb-12"><div className="h-24 rounded-lg bg-stone/20 animate-pulse" /></div>;
  }
  if (!stats) return null;

  const v = stats.visitors || {};
  const p = stats.paying || {};

  return (
    <section className="mb-12" data-testid="admin-stats-panel">
      <h2 className="text-xl font-serif font-normal tracking-tight text-ivory mb-1">Site analytics</h2>
      <p className="text-xs text-ash font-sans mb-5">Anonymous visitor counts and revenue at a glance. Privacy-safe — no cookies, IPs hashed.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          icon={Eye}
          label="Visitors today"
          value={(v.today?.unique ?? 0).toLocaleString()}
          hint={`${(v.today?.page_views ?? 0).toLocaleString()} page views`}
          accent="text-gold"
        />
        <StatTile
          icon={Users}
          label="Last 7 days"
          value={(v.last_7d?.unique ?? 0).toLocaleString()}
          hint={`${(v.last_7d?.page_views ?? 0).toLocaleString()} page views`}
          accent="text-sky-300"
        />
        <StatTile
          icon={TrendUp}
          label="Last 30 days"
          value={(v.last_30d?.unique ?? 0).toLocaleString()}
          hint={`${(v.last_30d?.page_views ?? 0).toLocaleString()} page views`}
          accent="text-emerald-300"
        />
        <StatTile
          icon={Users}
          label="All-time unique visits"
          value={(v.all_time?.unique ?? 0).toLocaleString()}
          hint={`${(v.all_time?.page_views ?? 0).toLocaleString()} total page views`}
          accent="text-violet-300"
        />

        <StatTile
          icon={CurrencyGbp}
          label="Revenue · 30 days"
          value={`£${(p.revenue_30d ?? 0).toFixed(2)}`}
          accent="text-gold"
        />
        <StatTile
          icon={Crown}
          label="Active subscribers"
          value={(p.active_subscribers ?? 0).toLocaleString()}
          accent="text-amber-300"
        />
        <StatTile
          icon={Users}
          label="Lifetime paying customers"
          value={(p.lifetime ?? 0).toLocaleString()}
          accent="text-rose-300"
        />
        <StatTile
          icon={TrendUp}
          label="Conversion rate"
          value={`${(stats.conversion_rate ?? 0).toFixed(1)}%`}
          hint="Paying / all-time visitors"
          accent="text-emerald-300"
        />
      </div>
    </section>
  );
};
